import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

const cardBaseSx = {
  borderRadius: 3,
  boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
  border: "1px solid #e2e8f0",
};

const onlyDigits = (value = "") => value.replace(/\D/g, "");

const formatCnpj = (value = "") => {
  const digits = onlyDigits(value).slice(0, 14);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatCep = (value = "") => {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatPhone = (value = "") => {
  const digits = onlyDigits(value).slice(0, 11);
  if (!digits) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export default function CompanyEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const lastCepRequestedRef = useRef("");
  const [formData, setFormData] = useState({
    name: "",
    trade_name: "",
    cnpj: "",
    state_registration: "",
    email: "",
    phone: "",
    address: "",
    address_number: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    city_code: "",
    payment_status: "",
    active: true,
  });

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies/${id}`);
      setFormData({
        name: response.data.name || "",
        trade_name: response.data.trade_name || response.data.settings?.trade_name || "",
        cnpj: formatCnpj(response.data.cnpj || ""),
        state_registration: response.data.state_registration || response.data.settings?.state_registration || "",
        email: response.data.email || "",
        phone: formatPhone(response.data.phone || ""),
        address: response.data.address || "",
        address_number: response.data.address_number || response.data.settings?.address_number || "",
        neighborhood: response.data.neighborhood || response.data.settings?.neighborhood || "",
        city: response.data.city || response.data.settings?.city || "",
        state: (response.data.state || response.data.settings?.state || "").toUpperCase().slice(0, 2),
        zip_code: formatCep(response.data.zip_code || response.data.settings?.zip_code || ""),
        city_code: onlyDigits(response.data.city_code || response.data.settings?.city_code || "").slice(0, 7),
        payment_status: response.data.payment_status || "",
        active: Boolean(response.data.active),
      });
    } catch (error) {
      toast.error("Erro ao carregar empresa");
      navigate("/admin/companies");
    } finally {
      setLoading(false);
    }
  };

  const fillAddressByCep = async (cepDigits) => {
    if (cepDigits.length !== 8 || lastCepRequestedRef.current === cepDigits) {
      return;
    }

    lastCepRequestedRef.current = cepDigits;

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepDigits}/json/`);
      const viaCep = response.data;

      if (viaCep?.erro) return;

      setFormData((prev) => ({
        ...prev,
        address: viaCep.logradouro || prev.address,
        neighborhood: viaCep.bairro || prev.neighborhood,
        city: viaCep.localidade || prev.city,
        state: (viaCep.uf || prev.state || "").toUpperCase().slice(0, 2),
        city_code: onlyDigits(viaCep.ibge || prev.city_code).slice(0, 7),
      }));
    } catch (_error) {
      // Falha do ViaCEP não deve bloquear o preenchimento manual
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let finalValue = value;
    if (name === "active") {
      finalValue = value === "true";
    }
    if (name === "cnpj") finalValue = formatCnpj(value);
    if (name === "phone") finalValue = formatPhone(value);
    if (name === "zip_code") {
      const cepDigits = onlyDigits(value).slice(0, 8);
      finalValue = formatCep(value);

      if (cepDigits.length === 8) {
        void fillAddressByCep(cepDigits);
      } else {
        lastCepRequestedRef.current = "";
      }
    }
    if (name === "city_code") finalValue = onlyDigits(value).slice(0, 7);
    if (name === "state") finalValue = value.toUpperCase().slice(0, 2);

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/admin/companies/${id}`, {
        ...formData,
        cnpj: onlyDigits(formData.cnpj),
        phone: onlyDigits(formData.phone),
        zip_code: onlyDigits(formData.zip_code),
        city_code: onlyDigits(formData.city_code),
      });
      toast.success("Empresa atualizada com sucesso!");
      navigate("/admin/companies");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao atualizar empresa"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid="company-edit-page">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Editar Empresa
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Atualize as informações da empresa
          </Typography>
        </Box>
        <Tooltip title="Voltar para lista" arrow>
          <IconButton
            onClick={() => navigate("/admin/companies")}
            size="small"
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Form */}
      <Card sx={cardBaseSx}>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            {/* GRID DO FORMULÁRIO */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(4, minmax(0, 1fr))",
                },
                gap: 3,
              }}
            >
              {/* 1ª linha – 4 colunas, todos com span 1 no desktop */}
              <TextField
                fullWidth
                label="Nome da Empresa"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="CNPJ"
                name="cnpj"
                value={formData.cnpj}
                onChange={handleChange}
                placeholder="00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Nome Fantasia"
                name="trade_name"
                value={formData.trade_name}
                onChange={handleChange}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Inscrição Estadual"
                name="state_registration"
                value={formData.state_registration}
                onChange={handleChange}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
                inputProps={{ maxLength: 15 }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Cód. Município IBGE"
                name="city_code"
                value={formData.city_code}
                onChange={handleChange}
                placeholder="Ex: 3550308"
                inputProps={{ maxLength: 7 }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="CEP"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                placeholder="00000-000"
                inputProps={{ maxLength: 9 }}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="UF"
                name="state"
                value={formData.state}
                onChange={handleChange}
                inputProps={{ maxLength: 2 }}
                placeholder="SP"
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Cidade"
                name="city"
                value={formData.city}
                onChange={handleChange}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Bairro"
                name="neighborhood"
                value={formData.neighborhood}
                onChange={handleChange}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              <TextField
                fullWidth
                label="Número"
                name="address_number"
                value={formData.address_number}
                onChange={handleChange}
                placeholder="S/N"
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 1" },
                }}
              />

              {/* 2ª linha – Endereço ocupando as 4 colunas no desktop */}
              <TextField
                fullWidth
                label="Endereço"
                name="address"
                value={formData.address}
                onChange={handleChange}
                multiline
                rows={2}
                sx={{
                  gridColumn: { xs: "1 / -1", md: "1 / span 4" },
                }}
              />

              {/* 3ª linha – dois selects, cada um ocupando 2 colunas no desktop */}
              <FormControl
                fullWidth
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 2" },
                }}
              >
                <InputLabel id="payment-status-label">
                  Status de Pagamento
                </InputLabel>
                <Select
                  labelId="payment-status-label"
                  label="Status de Pagamento"
                  name="payment_status"
                  value={formData.payment_status}
                  onChange={handleChange}
                >
                  <MenuItem value="periodo_teste">Período de Teste</MenuItem>
                  <MenuItem value="em_dia">Em Dia</MenuItem>
                  <MenuItem value="em_atraso">Em Atraso</MenuItem>
                  <MenuItem value="bloqueada">Bloqueada</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                sx={{
                  gridColumn: { xs: "1 / -1", md: "span 2" },
                }}
              >
                <InputLabel id="company-active-label">
                  Status da Empresa
                </InputLabel>
                <Select
                  labelId="company-active-label"
                  label="Status da Empresa"
                  name="active"
                  value={String(formData.active)}
                  onChange={handleChange}
                >
                  <MenuItem value="true">Ativa</MenuItem>
                  <MenuItem value="false">Inativa</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Ações */}
            <Box mt={4} display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={saving}
              >
                {saving ? "Salvando..." : "Salvar Alterações"}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/admin/companies")}
                disabled={saving}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
